import { monthsAbbr } from "$app/constants"
/**
 * Finds the difference in days between 2 dates
 * @param {Date} d2
 * @param {Date} d1
 * @returns {Number}
 */
export function differenceInDays(d2: Date | string, d1: Date | string): Number {
  try {
    if (typeof d2 === "string") {
      d2 = new Date(d2)
    }
    if (typeof d1 === "string") {
      if (d1 === null || d1 === "today") {
        d1 = new Date(`${new Date().toJSON().split("T")[0]}T00:00:00`)
      } else {
        d1 = new Date(d1)
      }
    }
    const timeDiff = d2.getTime() - d1.getTime()
    const days = Math.round(timeDiff / (1000 * 3600 * 24))

    return days
  } catch (e) {
    return 0
  }
}

/**
 * returns a formatted date
 * @param {Date} date object
 * @param {Object} options
 * @returns {String}
 * date.toLocaleDateString('en-US', {month: 'short', year: 'numeric', day: 'numeric' | '2-digit'})
 */
export function formatDate(
  date: string | Date,
  options?: string | Record<string, unknown>
): string {
  let newOpts = {}
  const defaultOpts = { year: "numeric", day: "2-digit", month: "2-digit" }

  if (typeof options === "object") {
    newOpts = { ...defaultOpts, ...options }
  } else if (options === "short") {
    newOpts = { ...defaultOpts, year: "2-digit" }
  } else {
    newOpts = { ...defaultOpts }
  }

  if (options === "db" && date instanceof Date) {
    const [dateDB, time] = date.toJSON().split("T")

    return dateDB
  }

  const location =
    typeof options === "object"
      ? (options?.location as string) || "en-US"
      : "en-US"
  const invalid =
    typeof options === "object"
      ? (options?.invalid as string) || "Invalid Date"
      : "Invalid Date"

  if (!date || date.toString() === "Invalid Date") {
    return invalid
  }

  try {
    let newDate: Date
    if (typeof date === "string") {
      const d = /\d{2}\:\d{2}/.test(date)
        ? new Date(date)
        : new Date(`${date}T00:00:00`)
      newDate = new Date(d)
    } else {
      newDate = date
    }
    return new Intl.DateTimeFormat(location, newOpts).format(newDate)
  } catch (err) {
    return invalid
  }
}

export function formatDateTime(date: string | Date, options?: any): string {
  const opts = {
    year: "numeric",
    day: "numeric",
    month: "short",
    minute: "2-digit",
    hour: "2-digit",
    hour12: true,
    ...options
  }

  return formatDate(date, opts)
}

function addS(num: number): string {
  return num == 1 ? "" : "s"
}

/**
 * Returns the time ago
 * @param {Date} date
 * @returns {String}
 */
export function timeAgo(date: Date | string): string {
  if (typeof date === "string") {
    date = new Date(date)
  }
  if (date.toString() === "Invalid Date") {
    return ""
  }

  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)

  let interval = Math.floor(seconds / 31536000)

  if (interval >= 1) {
    return interval + ` year${addS(interval)} ago`
  }
  interval = Math.floor(seconds / 2592000)
  if (interval >= 1) {
    return interval + ` month${addS(interval)} ago`
  }
  interval = Math.floor(seconds / 86400)
  if (interval >= 1) {
    return interval + ` day${addS(interval)} ago`
  }
  interval = Math.floor(seconds / 3600)
  if (interval >= 1) {
    return interval + ` hour${addS(interval)} ago`
  }
  interval = Math.floor(seconds / 60)
  if (interval >= 1) {
    return interval + ` minute${addS(interval)} ago`
  }

  return "just now"
}

export function timeAgoAbbr(date: Date | string): string {
  if (typeof date === "string") {
    date = new Date(date)
  }
  if (date.toString() === "Invalid Date") {
    return ""
  }

  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)
  let interval = seconds / 86400

  if (interval >= 1) {
    return `${Math.floor(interval)}d`
  }
  interval = seconds / 3600
  if (interval >= 1) {
    return `${Math.floor(interval)}h`
  }
  interval = seconds / 60
  if (interval >= 1) {
    return `${Math.floor(interval)}m`
  }

  return "now"
}

export function timeAgoOrDate(date: Date | string, days = 2): string {
  if (typeof date === "string") {
    date = new Date(date)
  }
  if (date.toString() === "Invalid Date") {
    return ""
  }

  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000)
  let interval = seconds / 86400

  if (interval <= days) {
    return timeAgo(date)
  } else {
    return formatDate(date)
  }
}

export function getQuarter(date: string | Date): string {
  let y, m, d

  if (date instanceof Date) {
    y = date.getFullYear()
    m = date.getMonth() + 1
  } else {
    ;[y, m, d] = date.split("-")
  }
  return `q${Math.ceil(+m / 3)}-${y}`
}

export function dateConverter(UNIX_timestamp: number): string {
  const a = new Date(UNIX_timestamp * 1000)
  const year = a.getFullYear()
  const month = monthsAbbr[a.getMonth()]
  const date = a.getDate()
  return month + " " + date + " " + year
}

export function displayDate(date: Date): string  {
  if (!date || date.toString() === "Invalid Date") {
    return "Select a date"
  } else {
    var months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec"
    ]
    var year = date.getFullYear()
    var month = months[date.getMonth()]
    var day = date.getDate()
    return month + " " + day + " " + year
  }
}
